import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { baseUrl, config, requestID } from "../../../common/utils";
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Snackbar,
} from "@mui/material";
import {
  Phone,
  Email,
  LocationOn,
  CalendarToday,
  AccessTime,
  Receipt as ReceiptIcon,
  CheckCircle,
  Hotel,
  Print,
  PictureAsPdf,
  ContentCopy,
  Image,
} from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

interface OrderItem {
  product_id: string;
  quantity: number;
  product_name: string;
  price: string;
  total_amount: string | null;
  item_status: string;
  description: string;
}

interface OrderData {
  order_id: string;
  order_code: string;
  order_date: string;
  mode_of_payment: string;
  payable_amount: string;
  balance_due: string;
  order_by: string;
  order_status: string;
  amount_paid: string;
  vat_per: string;
  vat_value: string;
  member_id: string;
  member_name: string;
  payment_status: number;
  otp_status: string;
  items: OrderItem[];
}

interface AppSettings {
  id: number;
  name: string;
  address: string;
  phone: string;
  email: string;
  user_id: string;
  business_id: string;
  size: string;
  business_type_id: string;
  targeted_savings_amount: string;
  target_cycle: string;
  collection_total_amount: string;
  bus_uuid: string;
  created_at: string;
  slogan: string;
  url: string;
  vat_per: string;
  vat_preference: number;
}

interface TransactionResponse {
  status: boolean;
  message: string;
  data: {
    orders: OrderData[];
    balance: number;
    appSettings: AppSettings;
  };
}

const FeedbackPage: React.FC = () => {
  const location = useLocation();
  const [transactionData, setTransactionData] = useState<TransactionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyImageSuccess, setCopyImageSuccess] = useState(false);
  
  const receiptRef = useRef<HTMLDivElement>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const query = new URLSearchParams(location.search);
  const name: string | null = query.get("name");
  const orderId: string | null = query.get("orderId");
  const orderReference: string | null = query.get("orderReference");

  useEffect(() => {
    if (!name || !orderId || !orderReference) {
      setError("Missing transaction parameters!");
      setLoading(false);
      return;
    }

    const verifyTransaction = async () => {
      try {
        const response = await axios.post(
          `${baseUrl()}/orderhistorys/verifyTransaction_online`,
          {
            name,
            orderId,
            orderReference,
            requestID: requestID(),
          },
          config
        );

        console.log("Transaction receipt details:", response.data);
        setTransactionData(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error verifying transaction:", error);
        setError("Failed to verify transaction. Please try again.");
        setLoading(false);
      }
    };

    verifyTransaction();
  }, [name, orderId, orderReference]);

  // Fixed print function
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Receipt-${transactionData?.data.orders[0].order_code}`,
    onAfterPrint: () => console.log('Print completed'),
    pageStyle: `
      @page {
        size: auto;
        margin: 10mm;
      }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        background: white;
        margin: 0;
        padding: 0;
      }
      .MuiPaper-root {
        box-shadow: none !important;
        border: 1px solid #ddd !important;
        page-break-inside: avoid;
      }
      .MuiTableContainer-root {
        page-break-inside: avoid;
      }
      .MuiPaper-elevation3 {
        margin: 0 !important;
        padding: 20px !important;
      }
    `,
  });

  // Fixed PDF generation with proper sizing
  const handleDownloadPDF = async () => {
    if (!receiptRef.current) return;
    
    try {
      // Temporarily modify styles for better PDF capture
      const originalOverflow = receiptRef.current.style.overflow;
      const originalWidth = receiptRef.current.style.width;
      const originalMaxWidth = receiptRef.current.style.maxWidth;
      
      receiptRef.current.style.overflow = 'visible';
      receiptRef.current.style.width = '800px';
      receiptRef.current.style.maxWidth = '800px';
      receiptRef.current.style.margin = '0 auto';
      
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true,
        useCORS: true,
        windowWidth: 1200,
        onclone: (clonedDoc, element) => {
          // Ensure cloned element has proper styling
          element.style.overflow = 'visible';
          element.style.width = '800px';
          element.style.maxWidth = '800px';
          element.style.margin = '0 auto';
        }
      });
      
      // Restore original styles
      receiptRef.current.style.overflow = originalOverflow;
      receiptRef.current.style.width = originalWidth;
      receiptRef.current.style.maxWidth = originalMaxWidth;
      
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate PDF dimensions to fit content properly
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      const pdf = new jsPDF({
        orientation: imgHeight > pageHeight ? 'portrait' : 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
      heightLeft -= pageHeight;
      
      // Add new pages if content overflows
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
        heightLeft -= pageHeight;
      }
      
      pdf.save(`receipt-${transactionData?.data.orders[0].order_code}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  // Copy as text function
  const handleCopyText = async () => {
    if (!transactionData) return;
    
    const order = transactionData.data.orders[0];
    const business = transactionData.data.appSettings;
    
    // Format receipt as plain text
    const receiptText = `
${business.name}
${business.slogan ? business.slogan : ''}
${business.address}
Tel: ${business.phone} | Email: ${business.email}
${business.url ? business.url : ''}
${business.bus_uuid}

RECEIPT #${order.order_code}
Date: ${formatDate(order.order_date)} ${formatTime(order.order_date)}
Order ID: ${order.order_id}

CUSTOMER DETAILS
----------------------------------------
Name: ${order.member_name}
Member ID: ${order.member_id}

ORDER ITEMS
----------------------------------------
${order.items.map(item => 
  `${item.product_name} x${item.quantity} @ ${formatCurrency(item.price)} = ${formatCurrency(parseFloat(item.price) * item.quantity)}`
).join('\n')}

PAYMENT SUMMARY
----------------------------------------
Subtotal: ${formatCurrency(order.payable_amount)}
${hasVAT() ? `VAT (${business.vat_per || order.vat_per}%): ${formatCurrency(order.vat_value)}` : ''}
Total Amount: ${formatCurrency(order.payable_amount)}
Amount Paid: ${formatCurrency(order.amount_paid)}
Balance Due: ${formatCurrency(order.balance_due)}

Payment Method: ${order.mode_of_payment.toUpperCase()}
Payment Status: ${order.payment_status === 1 ? "PAID" : "PENDING"}

Thank you for choosing ${business.name}!
This is a computer-generated receipt. No signature is required.
    `;
    
    try {
      await navigator.clipboard.writeText(receiptText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  // New function to copy as image
  const handleCopyImage = async () => {
    if (!receiptRef.current) return;
    
    try {
      // Temporarily modify styles for better image capture
      const originalOverflow = receiptRef.current.style.overflow;
      const originalWidth = receiptRef.current.style.width;
      
      receiptRef.current.style.overflow = 'visible';
      receiptRef.current.style.width = '600px'; // Optimal width for image copy
      
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true,
        useCORS: true,
      });
      
      // Restore original styles
      receiptRef.current.style.overflow = originalOverflow;
      receiptRef.current.style.width = originalWidth;
      
      // Convert canvas to blob and copy to clipboard
      canvas.toBlob(async (blob) => {
        if (blob) {
          try {
            await navigator.clipboard.write([
              new ClipboardItem({
                [blob.type]: blob
              })
            ]);
            setCopyImageSuccess(true);
            setTimeout(() => setCopyImageSuccess(false), 3000);
          } catch (err) {
            console.error("Failed to copy image:", err);
          }
        }
      });
    } catch (error) {
      console.error("Error generating image:", error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-NG', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(numAmount);
  };

  const hasVAT = (): boolean => {
    if (!transactionData) return false;
    const order = transactionData.data.orders[0];
    return order.vat_value && parseFloat(order.vat_value) > 0;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Processing your payment...
        </Typography>
      </Container>
    );
  }

  if (error || !transactionData?.status) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || transactionData?.message || "Transaction verification failed"}
        </Alert>
      </Container>
    );
  }

  const order = transactionData.data.orders[0];
  const business = transactionData.data.appSettings;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Success Message */}
      <Alert 
        icon={<CheckCircle fontSize="inherit" />} 
        severity="success" 
        sx={{ mb: 3 }}
      >
        Payment Successful! Your transaction has been completed.
      </Alert>

      {/* Action Buttons */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        justifyContent="flex-end" 
        sx={{ mb: 3 }}
      >
        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={() => handlePrint()}
          sx={{ bgcolor: '#1976d2' }}
        >
          Print Receipt
        </Button>
        <Button
          variant="contained"
          startIcon={<PictureAsPdf />}
          onClick={handleDownloadPDF}
          sx={{ bgcolor: '#dc3545', '&:hover': { bgcolor: '#c82333' } }}
        >
          Download PDF
        </Button>
        <Button
          variant="contained"
          startIcon={<ContentCopy />}
          onClick={handleCopyText}
          sx={{ bgcolor: '#28a745', '&:hover': { bgcolor: '#218838' } }}
        >
          Copy Text
        </Button>
        <Button
          variant="contained"
          startIcon={<Image />}
          onClick={handleCopyImage}
          sx={{ bgcolor: '#6f42c1', '&:hover': { bgcolor: '#5a32a3' } }}
        >
          Copy as Image
        </Button>
      </Stack>

      {/* Success Snackbars */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Receipt copied to clipboard!
        </Alert>
      </Snackbar>

      <Snackbar
        open={copyImageSuccess}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Receipt image copied to clipboard!
        </Alert>
      </Snackbar>

      {/* Print Version (hidden) */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          <ReceiptContent 
            order={order} 
            business={business} 
            formatDate={formatDate}
            formatTime={formatTime}
            formatCurrency={formatCurrency}
            hasVAT={hasVAT}
            orderId={orderId}
          />
        </div>
      </div>

      {/* Visible Receipt */}
      <div ref={receiptRef}>
        <ReceiptContent 
          order={order} 
          business={business} 
          formatDate={formatDate}
          formatTime={formatTime}
          formatCurrency={formatCurrency}
          hasVAT={hasVAT}
          orderId={orderId}
        />
      </div>
    </Container>
  );
};

// Extracted receipt content component with improved vertical alignment
const ReceiptContent: React.FC<{
  order: OrderData;
  business: AppSettings;
  formatDate: (date: string) => string;
  formatTime: (date: string) => string;
  formatCurrency: (amount: string | number) => string;
  hasVAT: () => boolean;
  orderId: string | null;
}> = ({ order, business, formatDate, formatTime, formatCurrency, hasVAT, orderId }) => {
  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 4, 
        backgroundColor: '#fff',
        position: 'relative',
        width: '100%',
        maxWidth: '1000px',
        margin: '0 auto',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '8px',
          background: 'linear-gradient(90deg, #1976d2 0%, #4caf50 100%)',
          borderTopLeftRadius: '4px',
          borderTopRightRadius: '4px',
        },
        '@media print': {
          boxShadow: 'none',
          border: '1px solid #ddd',
          margin: 0,
          padding: '20px',
        }
      }}
    >
      {/* Header with Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Hotel sx={{ fontSize: 48, color: '#1976d2', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {business.name}
            </Typography>
            {business.slogan && (
              <Typography variant="subtitle2" color="text.secondary">
                {business.slogan}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Chip 
            icon={<ReceiptIcon />} 
            label="PAID" 
            color="success" 
            sx={{ fontWeight: 'bold' }}
          />
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Receipt #{order.order_code}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            Business ID: {business.bus_uuid}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Business Contact Info - Full Width with vertical alignment */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LocationOn sx={{ fontSize: 20, color: '#666', mr: 1, flexShrink: 0 }} />
              <Typography variant="body2">{business.address}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { md: 'center' } }}>
              <Phone sx={{ fontSize: 20, color: '#666', mr: 1, flexShrink: 0 }} />
              <Typography variant="body2">{business.phone}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { md: 'flex-end' } }}>
              <Email sx={{ fontSize: 20, color: '#666', mr: 1, flexShrink: 0 }} />
              <Typography variant="body2">{business.email}</Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Transaction Details - Full Width Strip with proper alignment */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 3, 
          backgroundColor: '#f8f9fa',
          borderLeft: '4px solid #1976d2',
          width: 'calc(100% + 64px)',
          mx: -4,
          px: 4,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarToday sx={{ fontSize: 16, color: '#666', mr: 0.5, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                Date: {formatDate(order.order_date)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AccessTime sx={{ fontSize: 16, color: '#666', mr: 0.5, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                Time: {formatTime(order.order_date)}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" noWrap>
              Order ID: {order.order_id.substring(0, 15)}...
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" color="text.secondary" noWrap>
              Transaction Ref: {orderId}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Customer Information - Full Width with vertical alignment */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 2, 
          mb: 3, 
          backgroundColor: '#f8f9fa',
          width: 'calc(100% + 64px)',
          mx: -4,
          px: 4,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          CUSTOMER DETAILS
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1">
                <strong>Name:</strong> {order.member_name}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1">
                <strong>Member ID:</strong> {order.member_id.substring(0, 20)}...
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Items Table with proper spacing */}
      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Order Items
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ py: 1.5 }}><strong>Item</strong></TableCell>
              <TableCell align="center" sx={{ py: 1.5 }}><strong>Quantity</strong></TableCell>
              <TableCell align="right" sx={{ py: 1.5 }}><strong>Unit Price</strong></TableCell>
              <TableCell align="right" sx={{ py: 1.5 }}><strong>Total</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {order.items.map((item, index) => (
              <TableRow key={index}>
                <TableCell sx={{ py: 1.5 }}>
                  <Typography variant="body1">{item.product_name}</Typography>
                  {item.description && (
                    <Typography variant="caption" color="text.secondary">
                      {item.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ py: 1.5 }}>{item.quantity}</TableCell>
                <TableCell align="right" sx={{ py: 1.5 }}>{formatCurrency(item.price)}</TableCell>
                <TableCell align="right" sx={{ py: 1.5 }}>
                  {formatCurrency(parseFloat(item.price) * item.quantity)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Payment Summary - Full Width with proper alignment */}
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 2, 
          backgroundColor: '#f8f9fa',
          width: 'calc(100% + 64px)',
          mx: -4,
          px: 4,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          PAYMENT SUMMARY
        </Typography>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="body2">Subtotal:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" align="right">
              {formatCurrency(order.payable_amount)}
            </Typography>
          </Grid>
          
          {/* VAT Section - Only displayed if vat_value > 0 */}
          {hasVAT() && (
            <>
              <Grid item xs={6}>
                <Typography variant="body2">VAT ({business.vat_per || order.vat_per}%):</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" align="right">
                  {formatCurrency(order.vat_value)}
                </Typography>
              </Grid>
            </>
          )}
          
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="h6">Total Amount:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6" align="right" color="primary">
              {formatCurrency(order.payable_amount)}
            </Typography>
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="body2">Amount Paid:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" align="right" color="success.main">
              {formatCurrency(order.amount_paid)}
            </Typography>
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="body2">Balance Due:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" align="right" color={parseFloat(order.balance_due) > 0 ? 'error.main' : 'text.secondary'}>
              {formatCurrency(order.balance_due)}
            </Typography>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <strong>Payment Method:</strong>&nbsp;{order.mode_of_payment.toUpperCase()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <strong>Payment Status:</strong>&nbsp;
                <Chip 
                  label={order.payment_status === 1 ? "PAID" : "PENDING"} 
                  size="small"
                  color={order.payment_status === 1 ? "success" : "warning"}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Footer */}
      <Divider sx={{ my: 3 }} />
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Thank you for choosing {business.name}!
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          This is a computer-generated receipt. No signature is required.
        </Typography>
        {business.url && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {business.url} | {business.email}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default FeedbackPage;